import { last } from "@prismicio/editor-support/Array"
import { map } from "@prismicio/editor-support/Set"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import type { FilterOption } from "@prismicio/editor-ui"
import { useState } from "react"

import { searchDocumentStatus } from "../../../core/document"
import {
  type DocumentMeta,
  getDocuments,
  getEnrichedSearchDocuments,
  getRepository,
  makeGetDocumentsQueryString,
  makeSearchFullTextDocumentsQueryString,
  searchFullTextDocuments
} from "../../../core/service"
import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig
} from "../../../EditorConfig"

export function useDocuments(params: {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
}) {
  const { searchTerm, tagsFilter, typesFilter, localesFilter } = params
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const {
    coreApiBaseUrl,
    documentSearchBaseUrl,
    authStrategy,
    status,
    repository
  } = config

  const { storageVersion } = useRequest(getRepository, [
    coreApiBaseUrl,
    authStrategy
  ])

  // NOTE: Document Search API is only supported by repos with storageVersion.major >= 7
  const useDocumentSearchAPI = storageVersion.major >= 7

  function getQueryString() {
    if (!useDocumentSearchAPI) {
      // When FF not enabled, use legacy way
      return makeGetDocumentsQueryString({
        tags: [...map(tagsFilter, (tag) => tag.id)],
        customTypes: [...map(typesFilter, (type) => type.id)],
        language: [...map(localesFilter, (locale) => locale.id)],
        term: [searchTerm]
      })
    }

    // Use new document list open search
    return makeSearchFullTextDocumentsQueryString({
      repository,
      searchTerm,
      customTypesIds: [...map(typesFilter, (type) => type.id)],
      tags: [...map(tagsFilter, (tag) => tag.id)],
      localeId: [...map(localesFilter, (locale) => locale.id)][0]
    })
  }

  const [documents, setDocuments] = useState<DocumentMeta[]>([])
  const [previousQueryString, setPreviousQueryString] = useState("")

  const queryString = getQueryString()
  const response = useRequest(searchLinkDocuments, [
    coreApiBaseUrl,
    documentSearchBaseUrl,
    authStrategy,
    queryString,
    status,
    useDocumentSearchAPI
  ])

  if (queryString !== previousQueryString) {
    // The search term or filters have changed, so the state should reset to avoid
    // duplicate documents in the list.
    setPreviousQueryString(queryString)
    setDocuments(response.results)
  } else if (
    response.results.length > 0 &&
    last(documents)?.id !== last(response.results)?.id
  ) {
    setDocuments((currDocuments) => currDocuments.concat(response.results))
  }

  return {
    documents,
    response
  }
}

// Don't need our cache to re-evaluate whenever we change browser tab.
// The cache is already updated based on users scrolling to the bottom & searching
cache.registerFetcherConfig(searchLinkDocuments, {
  cacheCapacity: 500,
  revalidationStrategy: { type: "never" }
})

async function searchLinkDocuments(
  coreApiBaseUrl: URL,
  documentSearchBaseUrl: URL,
  authStrategy: AuthStrategy,
  queryString: string,
  status: keyof typeof searchDocumentStatus,
  useDocumentSearchAPI: boolean
) {
  const statusSearchFilters = searchDocumentStatus[status]

  if (!useDocumentSearchAPI) {
    // HACK: because this is the legacy version, and the pagination gone is gone
    // Define an high limit
    const limit = 200
    const documents = await getDocuments(
      coreApiBaseUrl,
      authStrategy,
      queryString,
      limit
    )
    return filterDocumentsByStatus(documents.results, statusSearchFilters)
  }

  const documents = await searchFullTextDocuments(
    documentSearchBaseUrl,
    authStrategy,
    queryString
  )

  const enrichedDocuments = await getEnrichedSearchDocuments(
    coreApiBaseUrl,
    authStrategy,
    documents.results
  )

  return filterDocumentsByStatus(enrichedDocuments, statusSearchFilters)
}

//HACK: status filter isn't working properly, filter documents by multiple searchDocumentStatus locally
function filterDocumentsByStatus(documents: DocumentMeta[], status: string[]) {
  const documentsFilteredByStatus = documents.flatMap((document) => {
    const filteredVersions = document.versions.filter((version) =>
      status.includes(version.status)
    )

    if (filteredVersions.length === 0) return []

    return {
      ...document,
      versions: filteredVersions
    }
  })

  return { results: documentsFilteredByStatus }
}
